import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Link, ExternalLink, HelpCircle } from "lucide-react";
import { useAnimals, linkChipLocal } from "@/lib/repo";
import { toast } from "sonner";
import { CATEGORY_LABEL } from "@/lib/utils";

const REASON_OPTIONS = [
  { value: "replacement", label: "Reemplazo del chip principal" },
  { value: "backup",      label: "Chip de respaldo" },
  { value: "recovered",   label: "Chip recuperado" },
];

/**
 * Modal mostrado durante el inventario cuando se escanea un chip que no está
 * registrado en ningún animal. Permite:
 *  - Vincularlo como chip secundario / reemplazo a un animal ya existente (por lomo).
 *  - Saltar a la pantalla de "Nuevo animal" con el chip prefijado.
 */
export function UnknownChipDialog({
  open, onOpenChange, chipCode, farmId, onLinked, onCreateNew, canEdit,
}) {
  const [search, setSearch] = useState("");
  const [animalId, setAnimalId] = useState("");
  const [reason, setReason] = useState("replacement");
  const [makePrimary, setMakePrimary] = useState(true);
  const [previousStatus, setPreviousStatus] = useState("damaged");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // Lee de Dexie (offline-first) los animales de la finca para el selector.
  const animals = useAnimals({ farm_id: farmId, status: "active", limit: 500 });

  useEffect(() => {
    if (!open) return;
    setAnimalId("");
    setSearch("");
  }, [open, farmId]);

  const filtered = animals.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      a.lomo_number?.toLowerCase().includes(q) ||
      a.chip_code?.toLowerCase().includes(q) ||
      a.ear_tattoo?.toLowerCase().includes(q) ||
      (a.chips || []).some((c) => c.rfid?.toLowerCase().includes(q))
    );
  });

  const selected = filtered.find((a) => a.id === animalId);

  const link = async () => {
    if (!animalId) {
      toast.error("Elegí el animal al que pertenece este chip");
      return;
    }
    setBusy(true);
    try {
      await linkChipLocal(animalId, {
        rfid: chipCode,
        reason,
        make_primary: makePrimary,
        previous_status: makePrimary ? previousStatus : null,
        notes: notes || `Vinculado durante inventario`,
      });
      toast.success(`Chip ${chipCode} vinculado a ${selected?.lomo_number || "animal"}`);
      onLinked?.(animalId);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.userMessage || "No pude vincular el chip");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="unknown-chip-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[hsl(var(--state-unknown))]">
            <HelpCircle size={20} />
            <DialogTitle className="font-editorial text-[22px] tracking-tight">Chip no registrado</DialogTitle>
          </div>
          <DialogDescription>
            El chip <span className="font-mono tabular-nums text-foreground">{chipCode}</span> no
            está vinculado a ningún animal. ¿Querés vincularlo como reemplazo de uno existente o
            registrar un animal nuevo?
          </DialogDescription>
        </DialogHeader>

        {canEdit ? (
          <div className="space-y-3">
            <div>
              <Label className="text-[12px] text-muted-foreground">Buscar animal (lomo, chip, tatuaje)</Label>
              <Input
                data-testid="unknown-chip-search"
                value={search}
                onChange={(e)=>setSearch(e.target.value)}
                placeholder="Ej: A-12"
                className="h-10"
              />
            </div>
            <div className="max-h-48 overflow-y-auto rounded-md border border-border divide-y divide-border" data-testid="unknown-chip-animal-list">
              {filtered.length === 0 && (
                <div className="p-3 text-[13px] text-muted-foreground text-center">Sin coincidencias</div>
              )}
              {filtered.slice(0, 50).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={()=>setAnimalId(a.id)}
                  data-testid={`unknown-chip-animal-option-${a.id}`}
                  className={`w-full text-left px-3 py-2 hover:bg-muted/60 transition-colors ${animalId === a.id ? "bg-muted/80 ring-1 ring-primary" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-heading text-[13.5px] font-semibold truncate">Lomo {a.lomo_number || "—"}</div>
                      <div className="font-mono text-[11.5px] tabular-nums text-muted-foreground truncate">{a.chip_code}</div>
                    </div>
                    {a.chips && a.chips.length > 1 && (
                      <Badge variant="outline" className="shrink-0">+{a.chips.length - 1}</Badge>
                    )}
                    {a.category && (
                      <span className="text-[11px] text-muted-foreground capitalize whitespace-nowrap">
                        {CATEGORY_LABEL[a.category] || a.category}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[12px] text-muted-foreground">Motivo</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger className="h-10" data-testid="unknown-chip-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REASON_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[12px] text-muted-foreground">¿Principal?</Label>
                <Select value={makePrimary ? "yes" : "no"} onValueChange={(v)=>setMakePrimary(v === "yes")}>
                  <SelectTrigger className="h-10" data-testid="unknown-chip-make-primary"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Sí, reemplaza al actual</SelectItem>
                    <SelectItem value="no">No, queda como secundario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {makePrimary && (
              <div>
                <Label className="text-[12px] text-muted-foreground">¿Qué pasa con el chip anterior?</Label>
                <Select value={previousStatus} onValueChange={setPreviousStatus}>
                  <SelectTrigger className="h-10" data-testid="unknown-chip-previous-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="damaged">Marcar como dañado</SelectItem>
                    <SelectItem value="lost">Marcar como perdido</SelectItem>
                    <SelectItem value="backup">Quedar como respaldo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[12px] text-muted-foreground">Notas (opcional)</Label>
              <Textarea
                data-testid="unknown-chip-notes"
                value={notes}
                onChange={(e)=>setNotes(e.target.value)}
                rows={2}
                placeholder="Ej: el chip viejo no respondía"
              />
            </div>
          </div>
        ) : (
          <div className="p-3 rounded-md bg-muted/60 text-[13px] text-foreground">
            Solo el dueño puede vincular chips. Avisale para que decida si es reemplazo o un animal nuevo.
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={()=>{ onOpenChange(false); onCreateNew?.(chipCode); }}
            data-testid="unknown-chip-create-new"
            disabled={!canEdit}
          >
            <ExternalLink size={14}/>Registrar como animal nuevo
          </Button>
          <Button
            onClick={link}
            disabled={busy || !animalId || !canEdit}
            data-testid="unknown-chip-link-submit"
          >
            <Link size={14}/>{busy ? "Vinculando…" : "Vincular a este animal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
